import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  IconButton,
  Chip,
  CircularProgress,
  Divider
} from '@material-ui/core'
import {
  Close as CloseIcon,
  Edit as EditIcon
} from '@material-ui/icons'
import { Alert } from '@material-ui/lab'
import { makeStyles } from '@material-ui/core/styles'
import * as iotAPI from '../../data/api/iot'

const useStyles = makeStyles(theme => ({
  dialog: {
    minWidth: 500
  },
  formField: {
    marginBottom: theme.spacing(2)
  },
  deviceInfo: {
    backgroundColor: theme.palette.grey[100],
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(2)
  },
  readOnlyField: {
    backgroundColor: theme.palette.grey[50],
    '& .MuiInputBase-input': {
      color: theme.palette.text.secondary
    }
  }
}))

const DEVICE_TYPES = [
  { value: 'magnet_monitor', label: 'Magnet Monitor' },
  { value: 'temperature_sensor', label: 'Temperature Sensor' },
  { value: 'pressure_sensor', label: 'Pressure Sensor' },
  { value: 'flow_sensor', label: 'Flow Sensor' }
]

const DEFAULT_CAPABILITIES = [
  'HLevel', 'HPress', 'Temperature', 'RH', 'WFlow', 'WTemp', 'comp'
]

export const IoTDeviceEditModal = ({ open, onClose, device, modalityId, onDeviceUpdated }) => {
  const classes = useStyles()
  const [formData, setFormData] = useState({
    deviceType: '',
    location: '',
    description: '',
    capabilities: [],
    devicePin: ''
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)

  useEffect(() => {
    if (open && device) {
      // Populate form with existing device data
      setFormData({
        deviceType: device.deviceType || '',
        location: device.location || '',
        description: device.description || '',
        capabilities: device.capabilities || [],
        devicePin: device.devicePin || ''
      })
      setError(null)
      setSuccess(false)
    }
  }, [open, device])

  const handleInputChange = (field) => (event) => {
    let value = event.target.value
    
    // Special handling for devicePin - convert to uppercase and limit to alphanumeric
    if (field === 'devicePin') {
      value = value.toUpperCase().replace(/[^A-Z0-9]/g, '')
    }
    
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleCapabilityChange = (event) => {
    setFormData(prev => ({
      ...prev,
      capabilities: event.target.value
    }))
  }

  const handleSubmit = async () => {
    if (!device || !modalityId) return

    setLoading(true)
    setError(null)
    setSuccess(false)

    try {
      // Prepare data for submission, only include devicePin if it's provided
      const submitData = { ...formData }
      if (!submitData.devicePin.trim()) {
        delete submitData.devicePin
      }
      
      // Make the actual API call to update the device
      await iotAPI.updateIoTDevice(modalityId, device.id, submitData)
      
      setSuccess(true)
      
      // Call the callback to refresh the device list
      if (onDeviceUpdated) {
        onDeviceUpdated()
      }
    } catch (err) {
      console.error('Error updating IoT device:', err)
      setError(err.message || 'Failed to update IoT device')
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setFormData({
      deviceType: '',
      location: '',
      description: '',
      capabilities: [],
      devicePin: ''
    })
    setError(null)
    setSuccess(false)
    onClose()
  }

  const isFormValid = formData.deviceType && formData.location && formData.description && 
    (!formData.devicePin || formData.devicePin.length === 6)

  if (!device) return null

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      className={classes.dialog}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center">
            <EditIcon style={{ marginRight: 8 }} />
            <Typography variant="h6">
              Edit IoT Device
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {success ? (
          <Box>
            <Alert severity="success" style={{ marginBottom: 16 }}>
              Device has been updated successfully!
            </Alert>
            
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Device ID: <strong>{device.deviceId}</strong>
            </Typography>
            
            <Typography variant="body2" color="textSecondary">
              All changes have been saved and applied to the device.
            </Typography>
          </Box>
        ) : (
          <Box>
            {error && (
              <Alert severity="error" style={{ marginBottom: 16 }}>
                {error}
              </Alert>
            )}

            <Box className={classes.deviceInfo}>
              <Typography variant="subtitle2" gutterBottom>
                Device Information:
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <strong>Device ID:</strong> {device.deviceId}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <strong>Status:</strong> {device.activatedAt ? 'Active' : 'Inactive'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <strong>Registration Date:</strong> {device.createdAt ? new Date(device.createdAt).toLocaleDateString() : 'N/A'}
              </Typography>
            </Box>

            <Divider style={{ margin: '16px 0' }} />

            <FormControl fullWidth className={classes.formField}>
              <InputLabel>Device Type</InputLabel>
              <Select
                value={formData.deviceType}
                onChange={handleInputChange('deviceType')}
              >
                {DEVICE_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Location"
              value={formData.location}
              onChange={handleInputChange('location')}
              placeholder="e.g., MRI Room 1, Scanner Bay A"
              className={classes.formField}
              required
            />

            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={handleInputChange('description')}
              placeholder="Brief description of the device"
              className={classes.formField}
              multiline
              rows={2}
              required
            />

            <TextField
              fullWidth
              label="Device PIN (Optional)"
              value={formData.devicePin}
              onChange={handleInputChange('devicePin')}
              placeholder="6-character alphanumeric PIN (e.g., ABC123)"
              className={classes.formField}
              inputProps={{
                maxLength: 6,
                style: { textTransform: 'uppercase' }
              }}
              error={formData.devicePin && formData.devicePin.length !== 6}
              helperText={
                formData.devicePin && formData.devicePin.length !== 6
                  ? "Device PIN must be exactly 6 characters"
                  : "Leave empty to remove device PIN"
              }
            />

            <FormControl fullWidth className={classes.formField}>
              <InputLabel>Capabilities</InputLabel>
              <Select
                multiple
                value={formData.capabilities}
                onChange={handleCapabilityChange}
                renderValue={(selected) => (
                  <Box display="flex" flexWrap="wrap" gap={0.5}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </Box>
                )}
              >
                {DEFAULT_CAPABILITIES.map((capability) => (
                  <MenuItem key={capability} value={capability}>
                    {capability}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Alert severity="info">
              <Typography variant="body2">
                <strong>Note:</strong> Changes to device configuration will take effect immediately. 
                Some changes may require the device to be reactivated.
              </Typography>
            </Alert>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {success ? (
          <Button onClick={handleClose} color="primary" variant="contained">
            Close
          </Button>
        ) : (
          <>
            <Button onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              color="primary"
              variant="contained"
              disabled={!isFormValid || loading}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              {loading ? 'Updating...' : 'Update Device'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default IoTDeviceEditModal
